import { useMemo, useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "@/App.css";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import DevaCore from "@/components/DevaCore";
import Capabilities from "@/components/Capabilities";
import ImmersiveShowcase from "@/components/ImmersiveShowcase";
import WhyDevamind from "@/components/WhyDevamind";
import Security from "@/components/Security";
import FinalCTA from "@/components/FinalCTA";
import Clients from "@/components/Clients";
import Footer from "@/components/Footer";

const ease = [0.22, 1, 0.36, 1];

function LoadingScreen({ onComplete }) {
  const [count, setCount] = useState(null);

  useEffect(() => {
    // Start countdown at 2.5s
    const t1 = setTimeout(() => setCount(3), 2500);
    const t2 = setTimeout(() => setCount(2), 3300);
    const t3 = setTimeout(() => setCount(1), 4100);
    const t4 = setTimeout(onComplete, 5000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [onComplete]);

  return (
    <motion.div
      className="fixed inset-0 z-[100] bg-[#05070A] flex flex-col items-center justify-center overflow-hidden"
      exit={{ opacity: 0 }}
      transition={{ duration: 1, ease }}
    >
      {/* Background Video - scaled up to crop watermark edges */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5, delay: 0.1 }}
        className="absolute inset-0"
      >
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute top-1/2 left-1/2 min-w-full min-h-full object-cover pointer-events-none"
          style={{
            transform: "translate(-50%, -50%) scale(1.15)",
            opacity: 0.35,
            mixBlendMode: "lighten",
          }}
        >
          <source src="https://customer-assets.emergentagent.com/job_deva-command-nexus/artifacts/krn4wk35_grok_video_2026-04-18-02-21-50.mp4" type="video/mp4" />
        </video>
      </motion.div>

      {/* Dark overlays to hide watermark + blend edges */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#05070A] via-transparent to-[#05070A]/60 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#05070A]/40 via-transparent to-[#05070A]/40 pointer-events-none" />
      <div className="absolute bottom-0 left-0 right-0 h-[25%] bg-gradient-to-t from-[#05070A] via-[#05070A]/95 to-transparent pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-[10%] bg-gradient-to-b from-[#05070A] to-transparent pointer-events-none" />

      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] md:w-[600px] md:h-[600px] rounded-full bg-[#00E5FF]/[0.04] blur-[120px] animate-pulse-slow" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[250px] h-[250px] md:w-[400px] md:h-[400px] rounded-full bg-[#7A5CFF]/[0.03] blur-[100px] animate-pulse-slow" style={{ animationDelay: "-2s" }} />

      {/* System label */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.5 }}
        className="sys-label mb-6 relative z-10"
      >
        SYS.BOOT //
      </motion.p>

      {/* DEVAMIND Logo */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 0.7, ease }}
        className="font-heading text-xl sm:text-2xl md:text-3xl tracking-[0.4em] uppercase text-white/90 mb-6 relative z-10 inline-flex"
      >
        <span>D</span>
        <span style={{ backgroundImage: "linear-gradient(135deg, #C49B3C, #F5D77A, #A67C28)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>E</span>
        <span style={{ backgroundImage: "linear-gradient(135deg, #00E5FF, #7A5CFF)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>V</span>
        <span style={{ backgroundImage: "linear-gradient(135deg, #7A5CFF, #FF2D9A)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>A</span>
        <span>MIND&nbsp;AI</span>
      </motion.p>

      {/* Main text */}
      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.4, delay: 1, ease }}
        className="font-heading text-2xl sm:text-3xl md:text-4xl font-extralight tracking-wide text-center px-6 relative z-10"
      >
        <span className="text-white/90">Enter The </span>
        <span className="bg-gradient-to-r from-[#00E5FF] via-[#7A5CFF] to-[#FF2D9A] bg-clip-text text-transparent">
          Tech Temple
        </span>
      </motion.h1>

      {/* Accent line */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 2, delay: 1.2, ease }}
        className="mt-8 w-16 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/40 to-transparent origin-center relative z-10"
      />

      {/* Loading bar */}
      <div className="mt-10 w-32 h-[1px] bg-white/[0.06] rounded-full overflow-hidden relative z-10">
        <motion.div
          initial={{ x: "-100%" }}
          animate={{ x: "100%" }}
          transition={{ duration: 2, delay: 0.8, ease: "linear", repeat: 1 }}
          className="h-full w-1/2 bg-gradient-to-r from-transparent via-[#00E5FF]/50 to-transparent"
        />
      </div>

      {/* Countdown */}
      <div className="mt-8 h-8 relative z-10 flex items-center justify-center">
        <AnimatePresence mode="wait">
          {count !== null && (
            <motion.span
              key={count}
              initial={{ opacity: 0, scale: 0.6, filter: "blur(8px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, scale: 1.4, filter: "blur(4px)" }}
              transition={{ duration: 0.35, ease }}
              className="font-mono text-sm tracking-[0.3em] uppercase text-[#00E5FF]/70"
            >
              {count > 1 ? `IN ${count}` : count === 1 ? "IN 1" : ""}
            </motion.span>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

/* ── Stagger Cascade reveal ──────────────────────────────────────── */
function SectionReveal({ children }) {
  const wrapRef = useRef(null);
  const triggered = useRef(false);

  useEffect(() => {
    if (!wrapRef.current) return;

    // Find all stagger targets: labels, cards, points, buttons, links
    const targets = wrapRef.current.querySelectorAll(
      '.sys-label, [data-testid*="card"], [data-testid*="point"], [data-testid*="cta-"], [data-testid*="footer-"], .grid, h2, h3'
    );

    // Dedupe: keep only the deepest targets (if a grid AND its children match, keep children only)
    const filtered = [];
    targets.forEach((el) => {
      // Skip grids that have matched children inside
      if (el.classList.contains('grid') && el.querySelectorAll('[data-testid]').length > 0) return;
      // Skip headings inside matched cards
      if ((el.tagName === 'H2' || el.tagName === 'H3') && el.closest('[data-testid]')) return;
      filtered.push(el);
    });

    // Pre-hide
    filtered.forEach((el) => {
      el.style.opacity = "0";
      el.style.transform = "translateY(50px) scale(0.94)";
      el.style.filter = "blur(6px)";
    });

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !triggered.current) {
          triggered.current = true;
          filtered.forEach((el, i) => {
            const delay = i * 180;
            el.style.transition = `opacity 1s cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 1s cubic-bezier(0.22,1,0.36,1) ${delay}ms, filter 1s cubic-bezier(0.22,1,0.36,1) ${delay}ms`;
            el.style.opacity = "1";
            el.style.transform = "translateY(0) scale(1)";
            el.style.filter = "blur(0px)";
          });
          observer.disconnect();
        }
      },
      { rootMargin: "-40px 0px" }
    );
    observer.observe(wrapRef.current);
    return () => observer.disconnect();
  }, []);

  return <div ref={wrapRef}>{children}</div>;
}

function GlobalBackground() {
  const particles = useMemo(
    () =>
      Array.from({ length: 30 }, (_, i) => ({
        id: i,
        left: `${Math.random() * 94 + 3}%`,
        delay: Math.random() * 20,
        duration: 18 + Math.random() * 14,
        size: 1 + Math.random() * 1.5,
        opacity: 0.1 + Math.random() * 0.2,
      })),
    []
  );

  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      {/* Grid */}
      <div className="grid-bg absolute inset-0" />

      {/* Particles */}
      {particles.map((p) => (
        <div
          key={p.id}
          className="particle"
          style={{
            left: p.left,
            top: "100%",
            width: `${p.size}px`,
            height: `${p.size}px`,
            background: `rgba(0, 229, 255, ${p.opacity})`,
            animation: `drift-up ${p.duration}s ${p.delay}s linear infinite`,
          }}
        />
      ))}
    </div>
  );
}

function App() {
  const [loading, setLoading] = useState(true);

  // Global click-to-pop: any element with class `pop-click` briefly pops + brightens when clicked/tapped.
  useEffect(() => {
    const onClick = (e) => {
      const el = e.target.closest(".pop-click");
      if (!el) return;
      el.classList.add("is-popped");
      window.setTimeout(() => el.classList.remove("is-popped"), 650);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  // When the Final CTA section enters the viewport, disable the mandatory
  // scroll-snap so the CTA → Footer transition is smooth (no snap trap at end of doc).
  useEffect(() => {
    if (loading) return;
    const cta = document.querySelector("#contact");
    if (!cta) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
          document.documentElement.classList.add("snap-off");
        } else {
          document.documentElement.classList.remove("snap-off");
        }
      },
      { threshold: [0, 0.25, 0.5, 1] }
    );
    observer.observe(cta);
    return () => {
      observer.disconnect();
      document.documentElement.classList.remove("snap-off");
    };
  }, [loading]);

  return (
    <div className="bg-[#05070A] min-h-screen text-white" style={{ overflowX: 'clip' }}>
      <AnimatePresence mode="wait">
        {loading && <LoadingScreen key="loader" onComplete={() => setLoading(false)} />}
      </AnimatePresence>

      {!loading && (
        <>
          <GlobalBackground />
          <Navbar />
          <main className="relative z-[1]">
            <Hero />
            <SectionReveal><DevaCore /></SectionReveal>
            <SectionReveal><Capabilities /></SectionReveal>
            <ImmersiveShowcase />
            <SectionReveal><Security /></SectionReveal>
            <SectionReveal><WhyDevamind /></SectionReveal>
            <SectionReveal><Clients /></SectionReveal>
            <SectionReveal><FinalCTA /></SectionReveal>
          </main>
          <SectionReveal><Footer /></SectionReveal>
        </>
      )}
    </div>
  );
}

export default App;
